import React, { useState, useEffect } from 'react';
import styles from './style.module.css';
import { AddMedicationSelectPageContent } from 'services/content';
import { useHistory, Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import infoCircle from 'assets/info-circle.svg';
import { BackPageInline } from 'components';
import useMedicationLookUp from 'hooks/useMedicationLookUp';
import useAddMedication from 'hooks/useAddMedication';
import { SET_ERROR_MESSAGE } from 'helpers/types';
import { Spinner } from 'components';

const AddMedicationSelect = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [chosenForm, setChosenForm] = useState('');
  const [displayForm, setDisplayForm] = useState(true);
  const [chosenStrength, setChosenStrength] = useState('');
  const [formError, setFormError] = useState(false);
  const [strengthError, setStrengthError] = useState(false);
  const [loadingForm, setLoadingForm] = useState(false);
  const [loadingStrength, setLoadingStrength] = useState(false);

  const isP9 = useSelector((state) => state.patient.isP9);
  const medication = useSelector((state) => state.addMedication.selectedMedication);

  const [
    ,
    formsFromMedication,
    strengthsFromMedicationAndForm,
    ,
    getFormsFromMedication,
    getStrengthsFromMedicationAndForm,
  ] = useMedicationLookUp();

  const [postMedication] = useAddMedication();

  useEffect(() => {
    if (isP9);
    if (Object.keys(medication).length === 0) {
      dispatch({
        type: SET_ERROR_MESSAGE,
        payload: {
          msg: 'Medication selection failed, please try again',
          newMessage: true,
          link: '/pharmacy/dashboard/add-medication',
          ctaText: 'Back to medication search',
        },
      });
      history.push('/pharmacy/error');
    }

    async function wrappedGetForms() {
      try {
        setLoadingForm(true);
        await getFormsFromMedication(medication);
        setLoadingForm(false);
      } catch (e) {
        setLoadingForm(false);
        dispatch({
          type: SET_ERROR_MESSAGE,
          payload: {
            msg: 'Medication selection failed, please try again',
            newMessage: true,
            link: '/pharmacy/dashboard/add-medication',
            ctaText: 'Back to medication search',
          },
        });
      }
    }

    wrappedGetForms();
  }, []);

  const updateForm = async (e, form) => {
    setChosenForm(form);
    setChosenStrength('');
    try {
      setLoadingStrength(true);
      await getStrengthsFromMedicationAndForm(medication, form);
      setLoadingStrength(false);
    } catch (e) {
      setLoadingStrength(false);
      dispatch({
        type: SET_ERROR_MESSAGE,
        payload: {
          msg: 'Medication selection failed, please try again',
          newMessage: true,
          link: '/pharmacy/dashboard/add-medication',
          ctaText: 'Back to medication search',
        },
      });
    }
  };

  const updateStrength = (e, strength) => {
    setChosenStrength(strength);
  };

  useEffect(() => {
    if (formsFromMedication?.forms?.length === 1) updateForm(null, formsFromMedication.forms[0]);
  }, [formsFromMedication]);

  useEffect(() => {
    if (strengthsFromMedicationAndForm?.strengths?.length === 1) {
      updateStrength(null, strengthsFromMedicationAndForm.strengths[0]);
      if (formsFromMedication.forms && formsFromMedication.forms.length === 1) setDisplayForm(false);
    }
  }, [strengthsFromMedicationAndForm]);

  const addMedication = async () => {
    setFormError(false);
    setStrengthError(false);
    if (chosenForm === '') {
      setFormError(true);
      return;
    }
    if (chosenStrength === '') {
      setStrengthError(true);
      return;
    }

    try {
      setLoadingForm(true);
      const payload = {
        name: medication.name,
        form: chosenForm,
        full_medication: chosenStrength,
      };
      await postMedication(payload);
      setLoadingForm(false);
      history.push('/pharmacy/dashboard');
    } catch (e) {
      setLoadingForm(false);
      dispatch({
        type: SET_ERROR_MESSAGE,
        payload: {
          msg: 'Medication selection failed, please try again',
          newMessage: true,
          link: '/pharmacy/dashboard/add-medication',
          ctaText: 'Back to medication search',
        },
      });
    }
  };

  const displayOptions = (option, selectedOption, updateOption, multipleOptions) => {
    return (
      <div
        tabIndex="0"
        onKeyPress={(event) => (event.key === 'Enter' ? updateOption(event, option) : null)}
        onClick={(e) => updateOption(e, option)}
        className={`${styles.box_container} ${multipleOptions && styles.clickable}`}
        key={option}>
        <div className={`${multipleOptions && styles.box_container__grid}`}>
          {multipleOptions && (
            <div className={styles.image_container}>
              <div className={`${styles.dot} ${selectedOption === option && styles.dot__active}`} />
            </div>
          )}
          <p className={styles.optionText}>{option}</p>
        </div>
      </div>
    );
  };

  return (
    <BackPageInline bplg="6" onBackClick={() => history.push('/pharmacy/dashboard/add-medication')}>
      <h2 className={styles.header}>{AddMedicationSelectPageContent.headingText}</h2>
      <div className={styles.infoContainer}>
        <img className={styles.infoContainer__icon} src={infoCircle} alt="Info Circle" />
        <p className={styles.infoContainer__paragraph}>{AddMedicationSelectPageContent.infoBoxText}</p>
      </div>
      <div className={styles.medicationContainer}>
        <h2 className={styles.subHeader}>{medication.name}</h2>
        <p className={styles.link}>
          <Link to={'/pharmacy/dashboard/add-medication'}>{AddMedicationSelectPageContent.changeMedicationText}</Link>
        </p>
      </div>
      <div className={styles.form}>
        {chosenForm && !displayForm ? (
          <>
            <h4
              className={`${styles.subHeader} ${
                formsFromMedication.forms && formsFromMedication.forms.length === 1 && styles.mb0
              }`}>
              {chosenForm}
            </h4>
            {formsFromMedication.forms && formsFromMedication.forms.length > 1 && (
              <p className={`coop-t-link ${styles.link}`} onClick={() => setDisplayForm(true)}>
                {AddMedicationSelectPageContent.changeFormText}
              </p>
            )}
          </>
        ) : (
          <>
            <h4 className={styles.subHeader}>{AddMedicationSelectPageContent.formHeadingText}</h4>
            {formError && <p className={styles.error_text}>{AddMedicationSelectPageContent.formErrorText}</p>}
            {!loadingForm ? (
              formsFromMedication?.forms?.map((form) =>
                displayOptions(
                  form,
                  chosenForm,
                  updateForm,
                  formsFromMedication.forms && formsFromMedication.forms.length > 1
                )
              )
            ) : (
              <Spinner />
            )}
          </>
        )}
      </div>
      {chosenForm !== '' && !displayForm && strengthsFromMedicationAndForm?.strengths.length > 0 && (
        <div className={styles.form}>
          {strengthsFromMedicationAndForm?.strengths && strengthsFromMedicationAndForm?.strengths?.length === 1 ? (
            <h4 className={`${styles.subHeader} ${styles.mb0}`}>{chosenStrength}</h4>
          ) : (
            <>
              <h4 className={styles.subHeader}>{AddMedicationSelectPageContent.strengthHeadingText}</h4>
              {strengthError && <p className={styles.error_text}>{AddMedicationSelectPageContent.strengthErrorText}</p>}
              {!loadingStrength ? (
                strengthsFromMedicationAndForm?.strengths?.map((strength) =>
                  displayOptions(
                    strength,
                    chosenStrength,
                    updateStrength,
                    strengthsFromMedicationAndForm?.strengths?.length > 1
                  )
                )
              ) : (
                <Spinner />
              )}
            </>
          )}
        </div>
      )}
      <button
        className={`coop-btn ${styles.button}`}
        onClick={() => {
          if (displayForm) {
            setFormError(false);
            if (chosenForm === '') {
              setFormError(true);
              return;
            }
            setDisplayForm(false);
            window.scrollTo(0, 0);
          } else addMedication();
        }}
        id="confirmlink">
        {displayForm ? AddMedicationSelectPageContent.nextButtonText : AddMedicationSelectPageContent.addButtonText}
      </button>
    </BackPageInline>
  );
};

export default AddMedicationSelect;
